import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from "react";
import {
  TimelineContext,
  useTimelineContext,
  useTimelineProviderValue,
} from "@/machines/context/timeline-context";
import {
  CursorStoreContext,
  useCursorEffect,
  useCursorStore,
  type CursorSnapshot,
  type CursorOrigin,
} from "@/machines/context/cursor-store.ts";
import { useGetTruckEvents, ROLLING_WINDOW_OPTIONS } from "@/machines/api/queries/events.queries.ts";
import { useTimelineEvents } from "@/machines/api/queries/use-timeline-events";
import { useDateTimeRangeStore, type DateTimeRangeValue } from "@/shared/stores/useDateTimeRangeStore.ts";
import type { MachineEvent } from "@/machines/types/event.ts";

/**
 * Seed the cursor to the latest event once data loads, so the video can fetch
 * footage even when the timeline panel itself isn't open.
 */
const CursorInit = ({ events }: { events?: MachineEvent[] }) => {
  const store = useCursorStore();
  // Read the cursor from the store INSIDE the effect (no per-tick re-render).
  // Keep the "seed only if null" logic; re-runs when `events` land, which is the
  // only trigger that matters (once seeded the guard makes it a no-op).
  useEffect(() => {
    if (store.getSnapshot().selectedTime != null || !events?.length) return;
    let latest = -Infinity;
    events.forEach((e) => {
      const t = new Date(e.endTime).getTime();
      if (t > latest) latest = t;
    });
    if (latest > -Infinity) store.setSelectedTime(latest, "external");
  }, [events, store]);
  return null;
};

/** Snaps the shared cursor (timeline + video) to a requested time. */
const SeekDriver = ({ seekTime }: { seekTime: number | null }) => {
  const { setSelectedTime } = useTimelineContext();
  useEffect(() => {
    if (seekTime != null) setSelectedTime(seekTime, "external");
  }, [seekTime, setSelectedTime]);
  return null;
};

/** A cursor jump smaller than this during playback is the video's own sync. */
const PLAYBACK_STEP_MS = 1000;

/** A backward cursor jump larger than this reads as a deliberate scrub-back. */
const LIVE_LEAVE_BACKWARD_MS = 2000;

/**
 * How close to the live edge still counts as "at the edge". A stop within this
 * of `liveEdge` is treated as "caught up to live" (stay armed, resume when more
 * footage lands); a stop further behind is a genuine mid-footage pause (leave).
 */
const LIVE_AT_EDGE_TOL_MS = 2000;

/**
 * A pause behind the edge must persist this long before it counts as a real
 * user pause. A continuous-rollover clip switch pauses while the next source
 * loads and then resumes — this window has to ride over those transients, or
 * ordinary rollover reads as "leave live".
 *
 * Sized from a measured rollover, not from the transient this was first written
 * for. 700ms was enough while live-follow pinned playback TO the edge: a stop
 * there is "caught up to now", `behindEdge` is false, and the timer never armed
 * at all — so the debounce only ever saw same-clip hiccups. The live playback
 * buffer changed that. Playback now deliberately trails the newest clip by
 * 60–180s, so EVERY rollover happens well behind the edge, arms this timer, and
 * has to finish before it fires. A rollover re-signs and reloads the source:
 * paired `emptied` events measured ~6s apart on ES5503-2.
 *
 * At 700ms the first rollover after load disarmed live-follow (observed 25s in,
 * never re-armed). That pauses the primary — and since the shared cursor is
 * driven by the primary's rAF loop, the whole live view stops with it: the
 * timeline domain, every follower tile, and both fetch paths in `useVideoData`,
 * which key on the cursor. Measured: cursor frozen 35s, then a 47s jump.
 */
const PAUSE_LEAVE_DEBOUNCE_MS = 8_000;

/**
 * Realtime live-follow watcher. It NEVER touches the shared cursor or the video
 * source — arming just best-effort starts playback (`requestPlay`) and lets the
 * primary keep playing the newest AVAILABLE footage (the never-blank + continuous
 * rollover path in the video). Yanking the cursor to a "live target" is exactly
 * what broke the source before (it forced the data hook to jump to an unbuffered
 * clip / raced playback into a false leave-live), so we don't do it.
 *
 * This driver only detects the user LEAVING live:
 *  - a deliberate scrub-back — a backward jump whose PREVIOUS position was at/behind
 *    the live edge (a real "I was watching live and dragged back"). The one-time
 *    catch-up pull, where playback first writes the cursor from the event-edge seed
 *    (which leads footage) back onto real footage, is ignored because its previous
 *    position sits AHEAD of the live edge.
 *  - a SUSTAINED mid-footage pause (debounced so rollover transients don't count).
 *
 * `liveEdge` = the newest clip's END (true live edge). `liveFootageStart` is the
 * newest clip's START; here it's used only as the "footage is known / we're
 * actually live" gate — leave-live is meaningless before any footage exists. Edit
 * mode suspends everything so editing stays calm.
 */
const LiveEdgeDriver = ({
  enabled,
  liveEdge,
  liveFootageStart,
  onLeaveLive,
}: {
  enabled: boolean;
  liveEdge: number | null;
  liveFootageStart: number | null;
  onLeaveLive?: () => void;
}) => {
  const store = useCursorStore();
  const { isVideoPlaying, isEditMode, requestPlay } = useTimelineContext();
  const active = enabled && !isEditMode;
  // Only watch for "leave live" once we're actually live (footage exists).
  const footageKnown = liveFootageStart != null;
  const cursorRef = useRef<number | null>(store.getSnapshot().selectedTime);
  // Origin of the PREVIOUS cursor value, so a backward jump can tell a genuine
  // user scrub (previous position was playback-established) from the startup /
  // go-live catch-up pull (previous position was a programmatic "external" seed
  // or seek). See the scrub-back guard below.
  const prevOriginRef = useRef<CursorOrigin>(store.getSnapshot().lastOrigin);
  const prevPlayingRef = useRef(isVideoPlaying);
  const prevActiveRef = useRef(false);
  // Pending "sustained pause -> leave live" timer (debounces rollover transients).
  const pauseLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Detect a deliberate scrub-back. Subscribe to the cursor store so this NEVER
  // re-renders per playback tick; `cursorRef` still tracks the latest cursor on
  // every change (the pause-leave effect below reads it).
  //
  // Ignore the one-time catch-up pull, where playback first writes the cursor
  // from the programmatic seed/seek (CursorInit's newest-EVENT end, or a go-live
  // seek to now) onto real FOOTAGE. That pull is a backward jump whenever the
  // seed sits at/behind the footage live edge — which happens routinely, since
  // events and footage are independent streams and the newest footage can be
  // fresher than the newest event. A position test alone can't tell it from a
  // real scrub-back (both leave the cursor behind the edge), so gate on ORIGIN
  // instead: the catch-up pull's PREVIOUS cursor is a programmatic "external"
  // seed/seek, whereas a genuine user timeline scrub-back drags away from a
  // playback-established position ("internal" RAF / "video" rollover). Only the
  // latter is a real "leave live".
  useEffect(() => {
    const handle = () => {
      const snap = store.getSnapshot();
      const cur = snap.selectedTime;
      const prev = cursorRef.current;
      const prevOrigin = prevOriginRef.current;
      cursorRef.current = cur;
      prevOriginRef.current = snap.lastOrigin;
      if (!active || !footageKnown || liveEdge == null) return;
      if (
        cur != null &&
        prev != null &&
        cur < prev - LIVE_LEAVE_BACKWARD_MS &&
        prev <= liveEdge + LIVE_AT_EDGE_TOL_MS &&
        // The PREVIOUS position must be playback-established too.
        //
        // `!== "external"` was not enough. A clip rollover writes "video", and the
        // RAF loop's next sample lands one clip BEHIND where it just was — the new
        // clip's currentTime restarts near zero — so a routine rollover produced a
        // 30,000ms backward "internal" write and read as a scrub-back. Captured
        // verbatim after a reload: {path: "backward-write", origin: "internal",
        // prevOrigin: "video", back: 30000}. It disarmed live-follow, which pauses
        // the primary and stops the shared cursor, leaving the playhead off the
        // left of the window (measured at -0.246 of the width) with nothing to
        // recover it.
        //
        // A genuine scrub-back drags away from a position playback itself
        // established, which is "internal". Requiring that excludes both the
        // programmatic seed ("external") and the rollover artefact ("video")
        // without weakening the real case.
        prevOrigin === "internal" &&
        // Only a TIMELINE SCRUB leaves live. Gating on the previous write's
        // origin alone was not enough: the catch-up pull this is meant to exempt
        // only looks programmatic while the cursor still sits on its seed. Once
        // the RAF loop has carried the cursor forward — which it does whenever
        // live-follow runs ahead of available footage — the previous write is
        // "internal", the exemption lapses, and the next clip-start write reads
        // as a scrub-back. Measured on ES5503-2: a 29,990ms pull (exactly one
        // clip) from 117ms PAST the live edge, prevOrigin "internal", which
        // disarmed live-follow and froze the whole view.
        //
        // A clip transition writes "video" and a programmatic seek writes
        // "external"; neither is a user leaving live. A timeline scrub writes
        // "internal", and the backward test above already excludes the RAF's own
        // forward "internal" writes — so this is the origin that means "the
        // operator dragged the playhead back".
        snap.lastOrigin === "internal"
      ) {
        onLeaveLive?.();
      }
    };
    return store.subscribe(handle);
  }, [active, footageKnown, liveEdge, onLeaveLive, store]);

  // On (re)arm: sync the pause baseline and best-effort start playback so the
  // primary follows live WITHOUT touching the cursor/source — it keeps playing
  // whatever recent footage it already has, just muted + marked live. Harmless if
  // the browser blocks autoplay; the "Go live" click provides the gesture.
  useEffect(() => {
    if (active && !prevActiveRef.current) {
      prevPlayingRef.current = isVideoPlaying;
      requestPlay();
    }
    prevActiveRef.current = active;
  }, [active, isVideoPlaying, requestPlay]);

  // Leave live on a SUSTAINED mid-footage pause. A stop within tolerance of the
  // live edge is "caught up to now" (stay armed). A stop meaningfully behind the
  // edge arms a debounced leave — cancelled if playback resumes within the window
  // (a rollover clip switch), fired only if the pause persists (a real user stop).
  useEffect(() => {
    if (active && footageKnown && prevPlayingRef.current && !isVideoPlaying) {
      const cur = cursorRef.current;
      const behindEdge =
        liveEdge != null && cur != null && cur < liveEdge - LIVE_AT_EDGE_TOL_MS;
      if (behindEdge && pauseLeaveTimerRef.current == null) {
        pauseLeaveTimerRef.current = setTimeout(() => {
          pauseLeaveTimerRef.current = null;
          onLeaveLive?.();
        }, PAUSE_LEAVE_DEBOUNCE_MS);
      }
    }
    if (isVideoPlaying && pauseLeaveTimerRef.current != null) {
      // Resumed (rollover transition, not a real pause) — cancel the pending leave.
      clearTimeout(pauseLeaveTimerRef.current);
      pauseLeaveTimerRef.current = null;
    }
    prevPlayingRef.current = isVideoPlaying;
  }, [active, footageKnown, isVideoPlaying, liveEdge, onLeaveLive]);

  // Drop any pending leave when follow disarms, and on unmount.
  useEffect(() => {
    if (!active && pauseLeaveTimerRef.current != null) {
      clearTimeout(pauseLeaveTimerRef.current);
      pauseLeaveTimerRef.current = null;
    }
  }, [active]);
  useEffect(
    () => () => {
      if (pauseLeaveTimerRef.current != null) clearTimeout(pauseLeaveTimerRef.current);
    },
    [],
  );

  return null;
};

/**
 * Mirrors the live cursor into a caller-owned ref.
 *
 * Unlike {@link CursorReporter} this filters nothing and renders nothing — it
 * only writes a ref. The page needs the cursor's CURRENT value at one moment
 * (has it gone stale while the tab was hidden?), not a stream of changes, and
 * `CursorReporter` deliberately suppresses playback ticks, which are exactly the
 * updates that keep this fresh.
 */
const CursorMirror = ({ cursorRef }: { cursorRef: MutableRefObject<number | null> }) => {
  useCursorEffect((snap) => {
    cursorRef.current = snap.selectedTime;
  });
  return null;
};

/** Surfaces cursor movement (scrub / video playback) up to the page. */
const CursorReporter = ({
  onCursorChange,
}: {
  onCursorChange: (time: number | null, origin: "internal" | "external" | "video") => void;
}) => {
  // isVideoPlaying via ref so play/pause never re-subscribes the cursor effect.
  const { isVideoPlaying } = useTimelineContext();
  const playingRef = useRef(isVideoPlaying);
  playingRef.current = isVideoPlaying;
  const prevTimeRef = useRef<number | null>(null);
  const report = useCallback(
    (snap: CursorSnapshot) => {
      const prevTime = prevTimeRef.current;
      prevTimeRef.current = snap.selectedTime;
      // A playback tick advances ~33ms; a click or seek lands further away.
      // Without this the page's nearest-alert scan — and its alert-detail
      // fetch — runs on every frame.
      if (
        playingRef.current &&
        snap.selectedTime != null &&
        prevTime != null &&
        Math.abs(snap.selectedTime - prevTime) < PLAYBACK_STEP_MS
      ) {
        return;
      }
      onCursorChange(snap.selectedTime, snap.lastOrigin);
    },
    [onCursorChange],
  );
  useCursorEffect(report);
  return null;
};

interface MachineTimelineProviderProps {
  deviceMxid: string;
  /** When set, snaps the timeline + video to this timestamp (e.g. an alert). */
  seekTime?: number | null;
  /** Fires whenever the shared cursor moves (used to follow the nearest alert). */
  onCursorChange?: (time: number | null, origin: "internal" | "external" | "video") => void;
  /**
   * Rolling window override (realtime mode) — replaces the global date range.
   * Marked rolling: previous data stays visible while a shifted window loads.
   */
  timeWindow?: DateTimeRangeValue | null;
  /**
   * Realtime "stay live": the newest available footage END (ms) = the true live
   * edge. Used only to tell a "caught up at the edge" stall from a genuine
   * mid-footage pause. Only acted on when `liveFollow` is set; ignored by
   * inactive/forwarding rows (only the self-owned shell provider drives the
   * shared cursor).
   */
  liveEdge?: number | null;
  /**
   * The newest available footage START (ms). Used only as the "footage is known /
   * we're live" gate for leave-live detection — the driver never seeks to it (a
   * cursor yank is what broke the source). Null until footage is known.
   */
  liveFootageStart?: number | null;
  /** Whether live-follow is currently armed (page-owned). */
  liveFollow?: boolean;
  /** Called when the user leaves live (scrub-back or pause) so the page can disarm. */
  onLeaveLive?: () => void;
  /**
   * The map stack's ACTIVE row. When true this provider forwards the ambient
   * (shell/video) timeline context to its children instead of owning fresh
   * state, so the active row shares one cursor + edit state with the docked
   * video and the stack header. Rendering the SAME element type for active and
   * inactive rows is what lets an active-machine switch re-render — not remount
   * — every row. Defaults to false (own isolated state).
   */
  active?: boolean;
  /**
   * Kept pointed at the current cursor value. Lets the page ask "how stale is the
   * live position?" without subscribing to a value that changes 30×/sec.
   */
  cursorRef?: MutableRefObject<number | null>;
  children: ReactNode;
}

/**
 * Shared `TimelineProvider` for the `/map` page — wraps the timeline panel AND
 * the (docked or floating) video so they stay synced through one context. Kept
 * out of the MapView subtree so map rendering isn't tied to the cursor.
 */
const MachineTimelineProvider = ({
  deviceMxid,
  seekTime = null,
  onCursorChange,
  timeWindow = null,
  liveEdge = null,
  liveFootageStart = null,
  liveFollow = false,
  onLeaveLive,
  active = false,
  cursorRef,
  children,
}: MachineTimelineProviderProps) => {
  const { value } = useDateTimeRangeStore();
  const fromDate = timeWindow?.fromDate ?? value.fromDate;
  const toDate = timeWindow?.toDate ?? value.toDate;
  const rollingOptions = timeWindow ? ROLLING_WINDOW_OPTIONS : undefined;

  const { data: events } = useTimelineEvents(
    { device_mxid: deviceMxid, start_time: fromDate, end_time: toDate, page: 1, itemsPerPage: 20000 },
    { ...rollingOptions, live: timeWindow != null },
  );
  const { data: truckEvents } = useGetTruckEvents({
    device_mxid: deviceMxid,
    start_time: fromDate,
    end_time: toDate,
    page: 1,
    itemsPerPage: 1000,
  }, { ...rollingOptions, fetchAll: true });

  // Own timeline state + cursor store for this machine. Always computed (stable
  // hook order), used by the shell provider and every inactive row; the active
  // row ignores it in favor of the ambient store + value. `/map` has no
  // `currentEvents` render consumer, so skip that per-frame computation.
  const { value: ownValue, cursorStore: ownStore } = useTimelineProviderValue(
    events?.data,
    truckEvents?.data as unknown as MachineEvent[] | undefined,
    { trackCurrentEvents: false },
  );

  // The active row forwards the ambient (shell/video) store AND value so it
  // shares one cursor + edit state with the docked video and the stack header.
  // BOTH contexts must forward together (they are always provided together). Both
  // branches render the SAME element types (CursorStoreContext.Provider >
  // TimelineContext.Provider), so an active-machine switch only flips the source
  // → every row re-renders instead of remounting (no timeline re-init, no refetch
  // flash). CursorInit/SeekDriver run only for a self-owned provider; the ambient
  // provider already drives the active machine's cursor seed + alert seek.
  const ambientValue = useContext(TimelineContext);
  const ambientStore = useContext(CursorStoreContext);
  const forwardAmbient = active && ambientValue != null && ambientStore != null;
  const contextValue = forwardAmbient ? ambientValue : ownValue;
  const cursorStore = forwardAmbient ? ambientStore : ownStore;

  return (
    <CursorStoreContext.Provider value={cursorStore}>
      <TimelineContext.Provider value={contextValue}>
        {!forwardAmbient && <CursorInit events={events?.data} />}
        {!forwardAmbient && <SeekDriver seekTime={seekTime} />}
        {!forwardAmbient && (
          <LiveEdgeDriver
            enabled={liveFollow}
            liveEdge={liveEdge}
            liveFootageStart={liveFootageStart}
            onLeaveLive={onLeaveLive}
          />
        )}
        {cursorRef && <CursorMirror cursorRef={cursorRef} />}
        {onCursorChange && <CursorReporter onCursorChange={onCursorChange} />}
        {children}
      </TimelineContext.Provider>
    </CursorStoreContext.Provider>
  );
};

export default MachineTimelineProvider;
